import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import HeaderBar from "../components/HeaderBar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

/**
 * V2.0.50 — Public Privacy Policy.
 *
 * Public route at /privacy. No auth required. Outlines:
 *   - Account data collected
 *   - Design canvas geometry coordinates retained for project history
 *   - Stripe used as the third-party payment processor (we never store
 *     full card numbers)
 *
 * Mirrors the visual language of /terms — same Section / HeaderBar
 * / Footer scaffolding so the two legal documents feel like siblings.
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-zinc-100" data-testid="privacy-page">
      <SEO
        title="Privacy Policy"
        description="How Blueprint AI collects, uses, and protects your account data, design canvas geometry, and payment details (processed by Stripe)."
        path="/privacy"
        themeColor="#0a0a0a"
      />
      <HeaderBar />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 lg:px-10 py-16">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-brass-400 transition-colors mb-10"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          // back to home
        </Link>

        <h1
          data-testid="privacy-page-title"
          className="font-mono text-3xl md:text-4xl text-brass-400 uppercase tracking-wide mb-3"
        >
          // Privacy Policy
        </h1>
        <p className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-12">
          Effective February 2026
        </p>

        <Section title="1. Overview">
          <p>
            Blueprint AI LLC (&ldquo;the Company,&rdquo; &ldquo;we,&rdquo;
            &ldquo;us,&rdquo; or &ldquo;our&rdquo;) operates the Blueprint AI
            software platform (&ldquo;the Platform&rdquo;) at{" "}
            <span className="text-zinc-100">blueprintaillc.com</span>. This
            Privacy Policy explains what information we collect, how we use
            it, who we share it with, and the choices you have.
          </p>
          <p>
            By creating an account, browsing the Platform, or purchasing a
            digital deliverable, you consent to the practices described
            below.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong className="text-zinc-100">Account data.</strong> When
              you sign in (via Google Sign-In) we receive and store the name,
              email address, and profile photo URL Google returns to us,
              plus a server-side session token. We do not see, receive, or
              store your Google password.
            </li>
            <li>
              <strong className="text-zinc-100">Design canvas data.</strong>{" "}
              Every project you create or modify is persisted as a
              structured blueprint JSON — including room geometry
              coordinates, wall positions, doors, windows, materials,
              styling preferences, and any associated chat history with our
              AI assistant. This lets you return to and iterate on your
              designs.
            </li>
            <li>
              <strong className="text-zinc-100">Usage telemetry.</strong> We
              log feature usage (e.g. PDF exports, IFC validations, plan
              checks) and a small set of operational events so we can debug
              the Platform and improve performance. We do not sell this
              telemetry.
            </li>
            <li>
              <strong className="text-zinc-100">Product feedback.</strong>{" "}
              Any feedback you submit via the in-app Feedback button is
              stored alongside the design context active at the moment of
              submission. We use it solely to improve the Platform.
            </li>
          </ul>
        </Section>

        <Section title="3. Payment Processing">
          <p>
            All payments on the Platform are processed by{" "}
            <strong className="text-zinc-100">Stripe, Inc.</strong>, our
            third-party payment processor. When you initiate a purchase, you
            are redirected to a Stripe-hosted checkout page; your full card
            number, CVC, and bank credentials are submitted{" "}
            <strong className="text-zinc-100">directly to Stripe</strong> and
            are <strong className="text-zinc-100">never seen or stored by
            Blueprint AI</strong>.
          </p>
          <p>
            We retain only the metadata Stripe returns to us — e.g. a
            transaction ID, the project identifier, the tier purchased, the
            last four digits of your card for receipt display, and the
            success / failure state of the charge. Stripe&apos;s own privacy
            terms apply to anything handled within their environment.
          </p>
        </Section>

        <Section title="4. How We Use Your Information">
          <ul className="list-disc pl-6 space-y-3">
            <li>To operate, secure, and improve the Platform.</li>
            <li>
              To generate, render, validate, and persist your architectural
              deliverables.
            </li>
            <li>
              To send transactional emails (purchase receipts, password
              reset links, design hand-off invitations).
            </li>
            <li>
              To detect, prevent, and respond to fraud, abuse, or security
              incidents.
            </li>
            <li>
              To analyze aggregate usage patterns so we can ship better
              features. Aggregate analytics are never tied back to an
              individual customer outside of the engineering team.
            </li>
          </ul>
        </Section>

        <Section title="5. Sharing & Disclosure">
          <p>
            We do <strong className="text-zinc-100">not</strong> sell your
            personal data. We disclose information only as follows:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong className="text-zinc-100">Service providers.</strong>{" "}
              Subprocessors that power the Platform — Stripe (payments),
              Resend (transactional email), Google (OAuth), and our cloud
              infrastructure provider — receive only the data they need to
              perform their function and are bound by their own privacy
              terms.
            </li>
            <li>
              <strong className="text-zinc-100">Legal compliance.</strong>{" "}
              We may disclose information to comply with a subpoena, court
              order, or other legal process; to enforce our Terms of
              Service; or to protect the rights, property, or safety of
              Blueprint AI, our users, or the public.
            </li>
            <li>
              <strong className="text-zinc-100">Business transfers.</strong>{" "}
              If Blueprint AI is involved in a merger, acquisition, or sale
              of assets, your information may be transferred — with notice
              to you — as part of that transaction.
            </li>
          </ul>
        </Section>

        <Section title="6. Data Retention & Account Deletion">
          <p>
            We retain account data and design canvas data for as long as
            your account is active. You may delete your account and every
            associated design at any time. You have two options:
          </p>
          <ul className="list-disc pl-6 space-y-3">
            <li>
              <strong className="text-zinc-100">Self-serve.</strong> Open
              your{" "}
              <Link to="/account" className="text-brass-400 hover:underline">
                Account
              </Link>{" "}
              page (top-right avatar menu) and click{" "}
              <strong className="text-zinc-100">Delete Account</strong> in the
              Danger Zone. The action requires you to type the word{" "}
              <span className="font-mono text-brass-400">DELETE</span> to
              confirm. Your account, all your designs, your project unlocks,
              your referral records, and all open sessions are removed
              immediately and irreversibly.
            </li>
            <li>
              <strong className="text-zinc-100">By email.</strong> Send a
              deletion request from the email address on file to{" "}
              <a
                href="mailto:info@blueprintaillc.com"
                className="text-brass-400 hover:underline"
              >
                info@blueprintaillc.com
              </a>
              . Email deletions are honored within 30 days, except where
              retention is required for tax / accounting / fraud-prevention
              obligations (typically up to 7 years for financial records).
            </li>
          </ul>
        </Section>

        <Section title="7. Cookies & Local Storage">
          <p>
            We use a small number of strictly necessary cookies / localStorage
            entries to keep you signed in and to remember UI preferences
            (e.g. the last viewed floor, measurement system, Accessibility
            mode). We do not use third-party advertising cookies.
          </p>
        </Section>

        <Section title="8. Children's Privacy">
          <p>
            The Platform is intended for users 18 years of age or older. We
            do not knowingly collect personal information from children
            under 13. If you believe a child has provided us information,
            please contact us at the address above and we will delete it.
          </p>
        </Section>

        <Section title="9. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. Material
            changes will be announced via the email address on file and on
            the Platform itself. The &ldquo;Effective&rdquo; date above
            reflects the current version.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Questions, requests, or concerns? Reach the team at{" "}
            <a
              href="mailto:info@blueprintaillc.com"
              data-testid="privacy-contact-email"
              className="text-brass-400 hover:underline"
            >
              info@blueprintaillc.com
            </a>
            .
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-12">
      <h2 className="font-mono text-base md:text-lg text-zinc-100 uppercase tracking-wide mb-5 border-l-2 border-brass-400 pl-3">
        {title}
      </h2>
      <div className="space-y-4 text-sm md:text-base leading-relaxed text-zinc-300">
        {children}
      </div>
    </section>
  );
}
